import React, { useEffect } from 'react';
import { useState } from 'react';
import { faPen } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { css } from 'styled-components';

import { useDevExTheme } from 'jf/common/themes/DevExTheme';

import { DevExInput } from './DevExInput';
import { DevExText } from './DevExText';

type EditableTextProps = {
  value: string | undefined;
  onChange: (val: string) => void;
  disabled?: boolean;
  size?: 'default' | 'large' | 'small';
  placeholder?: string;
  inputStyle?: React.CSSProperties;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  dataCy?: string;
};

export const EditableText: React.FC<EditableTextProps> = ({
  value,
  onChange,
  disabled,
  size,
  placeholder,
  inputStyle,
  onFocus,
  dataCy,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const theme = useDevExTheme();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const fontSize = size === 'large' ? theme.variable.fontSize.xl : theme.variable.fontSize.sm;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(event.target as Node) && isEditing) {
        setIsEditing(false); // Close editing mode when clicking outside
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing]);
  if (isEditing) {
    return (
      <div ref={inputRef}>
        <DevExInput
          onChange={(value) => onChange(value)}
          value={value ?? ''}
          placeholder={placeholder}
          disabled={disabled}
          embedded
          onFocus={onFocus}
          autoFocus
          onPressEnter={() => setIsEditing(false)}
          css={css`
            height: ${fontSize} !important;
            font-size: ${fontSize} !important;
          `}
          style={inputStyle}
          data-cy={`${dataCy}-input`}
        />
      </div>
    );
  }
  return (
    <DevExText
      editable={
        !disabled
          ? {
              icon: <FontAwesomeIcon icon={faPen} color={theme.color.brand.active} />,
              enterIcon: null,
              editing: isEditing,
              onStart: () => setIsEditing(true),
              triggerType: ['icon', 'text'],
            }
          : false
      }
      fontFamily="secondary"
      style={{ fontSize }}
      data-cy={`${dataCy}-text`}
    >
      {value ?? placeholder ?? ''}
    </DevExText>
  );
};
